import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";
import { useAuth } from "@/context/auth";
import { useSecondFactorGate } from "@/context/second-factor-gate";
import type { paths } from "@/types/api/api-types.ts";

// Create fetch client with base configuration
const fetchClient = createFetchClient<paths>({
  baseUrl: process.env.NEXT_PUBLIC_API_URL || "http://localhost:7000",
  headers: {
    Authorization: `Bearer ${useAuth.getState().token}`,
  },
});

// Middleware 1: Add auth token to requests
fetchClient.use({
  onRequest({ request }) {
    const token = useAuth.getState().token;
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    return request;
  },
});

// Middleware 2: Handle 401 responses by clearing stale auth state
// Prevents "ghost sessions" where user appears logged in with expired JWT.
let isLoggingOut = false;

fetchClient.use({
  async onResponse({ response, request }) {
    if (response.status !== 401) return;

    // Don't logout during login attempts — only for authenticated requests that fail
    const url = new URL(request.url);
    if (url.pathname.includes("/auth/login")) return;

    // Guard against multiple concurrent logouts
    if (isLoggingOut) return;
    isLoggingOut = true;

    try {
      await useAuth.getState().logout();
    } finally {
      isLoggingOut = false;
    }
  },
});

// Middleware 3: Detect the 2FA enforcement gate. A session that predates 2FA
// enforcement gets 403 `second_factor_required` on protected routes; raise the
// gate so the app prompts for the second-factor password (do NOT log out).
fetchClient.use({
  async onResponse({ response, request }) {
    if (response.status !== 403) return;

    const url = new URL(request.url);
    // The 2FA endpoints themselves are never gated.
    if (url.pathname.startsWith("/auth/")) return;

    try {
      const body = await response.clone().json();
      const code = body?.errorData?.message ?? body?.error;
      if (code === "second_factor_required") {
        useSecondFactorGate.getState().setPending(true);
      }
    } catch {
      // Non-JSON 403 — not our gate signal.
    }
  },
});

export const $api = createClient(fetchClient);

export { fetchClient };
